import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarRange,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  FileBarChart,
  FileText,
  HardHat,
  Home,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  Plus,
  Receipt,
  Search,
  Settings,
  Sparkles,
  UserRound,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { Button, Modal } from "../ui";
import { useAppStore } from "../../store/useAppStore";
import { useAuth } from "../../features/auth/AuthProvider";
import { BrandLogo } from "../branding/BrandLogo";

const groups = [
  {
    label: "VISÃO GERAL",
    items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "COMERCIAL",
    items: [
      { to: "/comercial/leads", label: "Leads", icon: Users },
      { to: "/clientes", label: "Clientes", icon: Building2 },
      {
        to: "/oportunidades/opp-1",
        label: "Oportunidades",
        icon: BriefcaseBusiness,
      },
      { to: "/medicoes", label: "Medições", icon: ClipboardCheck },
      { to: "/ai-studio", label: "Simulações IA", icon: Sparkles },
      { to: "/orcamentos", label: "Orçamentos", icon: FileText },
    ],
  },
  {
    label: "OBRAS",
    items: [
      { to: "/obras", label: "Todas as obras", icon: HardHat },
      {
        to: "/obras/project-1/cronograma",
        label: "Cronograma",
        icon: CalendarRange,
      },
      {
        to: "/obras/project-1/atividades",
        label: "Atividades",
        icon: ClipboardCheck,
      },
      {
        to: "/obras/project-1/diario",
        label: "Diário de obra",
        icon: FileText,
      },
    ],
  },
  {
    label: "FINANCEIRO",
    items: [
      { to: "/financeiro", label: "Visão financeira", icon: CircleDollarSign },
      { to: "/financeiro/receber", label: "A receber", icon: WalletCards },
      { to: "/financeiro/pagar", label: "A pagar", icon: Receipt },
      { to: "/financeiro/despesas", label: "Despesas", icon: CircleDollarSign },
    ],
  },
  {
    label: "GESTÃO",
    items: [
      { to: "/relatorios", label: "Relatórios", icon: FileBarChart },
      { to: "/configuracoes", label: "Configurações", icon: Settings },
    ],
  },
];
const routeNames: Record<string, string> = {
  dashboard: "Dashboard",
  comercial: "Comercial",
  leads: "Leads",
  clientes: "Clientes",
  oportunidades: "Oportunidades",
  medicoes: "Medições",
  "ai-studio": "Empreita AI Studio",
  orcamentos: "Orçamentos",
  obras: "Obras",
  resumo: "Resumo",
  cronograma: "Cronograma",
  atividades: "Atividades",
  diario: "Diário",
  fotos: "Fotos",
  documentos: "Documentos",
  equipe: "Equipe",
  financeiro: "Financeiro",
  receber: "A receber",
  pagar: "A pagar",
  despesas: "Despesas",
  "fluxo-caixa": "Fluxo de caixa",
  cadastros: "Cadastros",
  servicos: "Serviços",
  materiais: "Materiais",
  fornecedores: "Fornecedores",
  relatorios: "Relatórios",
  configuracoes: "Configurações",
  empresa: "Empresa",
  usuarios: "Usuários",
};

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const collapsed = useAppStore((state) => state.sidebarCollapsed);
  const setCollapsed = useAppStore((state) => state.setSidebarCollapsed);
  const notifications = useAppStore((state) => state.notifications);
  const markRead = useAppStore((state) => state.markNotificationRead);
  const markAll = useAppStore((state) => state.markAllNotificationsRead);
  const onboardingSeen = useAppStore((state) => state.onboardingSeen);
  const completeOnboarding = useAppStore((state) => state.completeOnboarding);
  const recentPaths = useAppStore((state) => state.recentPaths);
  const addRecentPath = useAppStore((state) => state.addRecentPath);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [palette, setPalette] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [query, setQuery] = useState("");
  useEffect(() => {
    setMobileMenu(false);
    addRecentPath(location.pathname);
  }, [addRecentPath, location.pathname]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(
        (event.target as HTMLElement).tagName,
      );
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPalette(true);
      }
      if (!typing && event.key.toLowerCase() === "n") setCreateOpen(true);
      if (event.key === "Escape") {
        setPalette(false);
        setCreateOpen(false);
        setNoticeOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  const breadcrumbParts = location.pathname.split("/").filter(Boolean);
  const breadcrumbs = breadcrumbParts.map((part, index) => ({
    label:
      routeNames[part] ??
      (part === "project-1"
        ? "Residência Oliveira"
        : part === "client-1"
          ? "João Oliveira"
          : part === "quote-1"
            ? "ORC-2026-0018"
            : part),
    path: `/${breadcrumbParts.slice(0, index + 1).join("/")}`,
  }));
  const results = useMemo(() => {
    const clients = useAppStore.getState().clients.map((item) => ({
      label: item.name,
      type: "Cliente",
      path: `/clientes/${item.id}`,
    }));
    const leads = useAppStore.getState().leads.map((item) => ({
      label: item.name,
      type: "Lead",
      path: "/comercial/leads",
    }));
    const projects = useAppStore.getState().projects.map((item) => ({
      label: item.name,
      type: "Obra",
      path: `/obras/${item.id}/resumo`,
    }));
    const quotes = useAppStore.getState().quotes.map((item) => ({
      label: item.code,
      type: "Orçamento",
      path: `/orcamentos/${item.id}`,
    }));
    return [...clients, ...leads, ...projects, ...quotes]
      .filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 8);
  }, [query, palette]);
  const unread = notifications.filter((item) => !item.read).length;
  const resultByPath = new Map(results.map((item) => [item.path, item]));
  const recentResults = recentPaths
    .map((path) => resultByPath.get(path))
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .slice(0, 3);
  return (
    <div className={`app-shell ${collapsed ? "is-collapsed" : ""}`}>
      <aside className={`sidebar ${mobileMenu ? "is-open" : ""}`}>
        <div className="sidebar__brand">
          <BrandLogo />
          {!collapsed && (
            <div>
              <strong>EmpreitaFlow</strong>
              <small>ConstruTech Reformas</small>
            </div>
          )}
          <button
            className="sidebar__mobile-close"
            onClick={() => setMobileMenu(false)}
            aria-label="Fechar menu"
          >
            <X />
          </button>
        </div>
        <nav aria-label="Navegação principal">
          {groups.map((group) => (
            <div className="nav-group" key={group.label}>
              {!collapsed && <span>{group.label}</span>}
              {group.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  title={collapsed ? label : undefined}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  <Icon size={18} />
                  {!collapsed && <span>{label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar__footer">
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            <span className="avatar">DM</span>
            {!collapsed && (
              <span>
                <strong>Diogo Mendes</strong>
                <small>Administrador</small>
              </span>
            )}
          </button>
        </div>
        <button
          className="collapse-button"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </button>
      </aside>
      {mobileMenu && (
        <button
          className="mobile-overlay"
          aria-label="Fechar menu"
          onClick={() => setMobileMenu(false)}
        />
      )}
      <div className="app-main">
        <header className="topbar">
          <button
            className="mobile-menu"
            onClick={() => setMobileMenu(true)}
            aria-label="Abrir menu"
          >
            <Menu />
          </button>
          <div className="breadcrumbs">
            {breadcrumbs.map((crumb, index) => (
              <span key={`${crumb.path}-${index}`}>
                {index > 0 && <i>/</i>}
                <button onClick={() => navigate(crumb.path)}>
                  {crumb.label}
                </button>
              </span>
            ))}
          </div>
          <button className="top-search" onClick={() => setPalette(true)}>
            <Search size={17} />
            <span>Pesquisar cliente, obra, orçamento...</span>
            <kbd>⌘ K</kbd>
          </button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={17} /> Criar
          </Button>
          <button
            className="icon-button notice-button"
            onClick={() => setNoticeOpen(!noticeOpen)}
            aria-label={`${unread} notificações não lidas`}
          >
            <Bell size={19} />
            {unread > 0 && <b>{unread}</b>}
          </button>
          {noticeOpen && (
            <div className="popover notifications">
              <header>
                <strong>Notificações</strong>
                <button onClick={markAll}>Marcar todas como lidas</button>
              </header>
              {notifications.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    markRead(item.id);
                    if (item.path) navigate(item.path);
                    setNoticeOpen(false);
                  }}
                  className={item.read ? "" : "unread"}
                >
                  <span />
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.description}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          <button
            className="profile-trigger"
            onClick={() => setProfileOpen(!profileOpen)}
            aria-label="Abrir menu do perfil"
          >
            <span className="avatar top-avatar">DM</span>
            <ChevronDown />
          </button>
          {profileOpen && (
            <div className="popover profile-menu">
              <div>
                <span className="avatar">DM</span>
                <p>
                  <strong>Diogo Mendes</strong>
                  <small>Administrador</small>
                </p>
              </div>
              <button
                onClick={() => {
                  navigate("/configuracoes/usuarios");
                  setProfileOpen(false);
                }}
              >
                <UserRound /> Meu perfil
              </button>
              <button
                onClick={() => {
                  navigate("/configuracoes");
                  setProfileOpen(false);
                }}
              >
                <Settings /> Configurações
              </button>
              <button
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
              >
                <LogOut /> Sair
              </button>
            </div>
          )}
        </header>
        <main className="page-content">
          <Outlet />
        </main>
      </div>
      <nav className="bottom-nav">
        <NavLink to="/dashboard">
          <Home />
          <span>Home</span>
        </NavLink>
        <NavLink to="/obras">
          <HardHat />
          <span>Obras</span>
        </NavLink>
        <button className="bottom-create" onClick={() => setCreateOpen(true)}>
          <Plus />
        </button>
        <NavLink to="/financeiro">
          <CircleDollarSign />
          <span>Financeiro</span>
        </NavLink>
        <button onClick={() => setMobileMenu(true)}>
          <Menu />
          <span>Mais</span>
        </button>
      </nav>
      <Modal
        open={palette}
        title="Busca rápida"
        onClose={() => setPalette(false)}
      >
        <div className="command">
          <div className="command__input">
            <Search />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Pesquisar em todo o EmpreitaFlow"
            />
          </div>
          <div className="command__results">
            <span className="command__section">PESQUISAR</span>
            {results.map((item) => (
              <button
                key={`${item.type}-${item.path}-${item.label}`}
                onClick={() => {
                  navigate(item.path);
                  setPalette(false);
                  setQuery("");
                }}
              >
                <span>
                  <Search size={16} />
                  {item.label}
                </span>
                <small>{item.type}</small>
              </button>
            ))}
            <span className="command__section">AÇÕES RÁPIDAS</span>
            {[
              { label: "Novo lead", path: "/comercial/leads?novo=1" },
              { label: "Nova despesa", path: "/financeiro/despesas?novo=1" },
              { label: "Novo orçamento", path: "/orcamentos/novo" },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  navigate(item.path);
                  setPalette(false);
                }}
              >
                <span>
                  <Plus size={16} />
                  {item.label}
                </span>
                <small>Ação</small>
              </button>
            ))}
            {recentResults.length > 0 && (
              <>
                <span className="command__section">ACESSADOS RECENTEMENTE</span>
                {recentResults.map((item) => (
                  <button
                    key={`recent-${item.path}`}
                    onClick={() => {
                      navigate(item.path);
                      setPalette(false);
                    }}
                  >
                    <span>
                      <History size={16} />
                      {item.label}
                    </span>
                    <small>{item.type}</small>
                  </button>
                ))}
              </>
            )}
          </div>
          <footer>
            Use ↑↓ para navegar · Enter para abrir · Esc para fechar
          </footer>
        </div>
      </Modal>
      <Modal
        open={createOpen}
        title="Criar novo"
        onClose={() => setCreateOpen(false)}
      >
        <div className="quick-create">
          {[
            { label: "Novo lead", path: "/comercial/leads?novo=1" },
            { label: "Novo cliente", path: "/clientes?novo=1" },
            { label: "Nova foto", path: "/obras/project-1/fotos?novo=1" },
            { label: "Nova despesa", path: "/financeiro/despesas?novo=1" },
            { label: "Novo RDO", path: "/obras/project-1/diario?novo=1" },
            { label: "Novo orçamento", path: "/orcamentos/novo" },
            { label: "Nova obra", path: "/obras" },
          ].map(({ label, path }) => (
            <button
              key={label}
              onClick={() => {
                navigate(path);
                setCreateOpen(false);
              }}
            >
              <Plus />
              <span>{label}</span>
              <ChevronRight />
            </button>
          ))}
        </div>
      </Modal>
      <Modal
        open={!onboardingSeen}
        title="Bem-vindo ao EmpreitaFlow"
        onClose={completeOnboarding}
      >
        <div className="onboarding">
          <p>
            Da primeira oportunidade à entrega, toda a operação no mesmo fluxo.
          </p>
          <div className="onboarding__steps">
            <div>
              <b>1</b>
              <span>
                <strong>Organize oportunidades</strong>
                <small>Saiba quem contatar e qual é a próxima ação.</small>
              </span>
            </div>
            <div>
              <b>2</b>
              <span>
                <strong>Transforme fotos em propostas</strong>
                <small>Meça, simule e apresente com clareza.</small>
              </span>
            </div>
            <div>
              <b>3</b>
              <span>
                <strong>Gerencie até a entrega</strong>
                <small>Conecte execução, equipe e resultado financeiro.</small>
              </span>
            </div>
          </div>
          <footer>
            <Button variant="ghost" onClick={completeOnboarding}>
              Pular
            </Button>
            <Button onClick={completeOnboarding}>Começar</Button>
          </footer>
        </div>
      </Modal>
    </div>
  );
}
